"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import PharmacySearchSelect, { PharmacyDetails } from "@/components/dosespot/pharmacy-search-select";
import { Button, Checkbox, Label } from "@/components/ui";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import type { DoseSpotPharmacySnapshot } from "@/types/dosespot";
import { useAddPatientPharmacyMutation } from "@/query/patient/pharmacies";
interface AddPharmacyDialogProps {
  patientId: string;
  onClose: () => void;
  /** Preselects "set as primary" when the patient has no pharmacy on file yet. */
  defaultSetAsPrimary: boolean;
}

/** Mounted only while open, so each add starts from an empty search. */
export default function AddPharmacyDialog(t0) {
  const $ = _c(40);
  const {
    patientId,
    onClose,
    defaultSetAsPrimary
  } = t0;
  const [pharmacy, setPharmacy] = useState();
  const [setAsPrimary, setSetAsPrimary] = useState(defaultSetAsPrimary);
  const addMutation = useAddPatientPharmacyMutation(patientId);
  let t1;
  if ($[0] !== addMutation.isPending || $[1] !== onClose) {
    t1 = () => {
      if (addMutation.isPending) {
        return;
      }
      onClose();
    };
    $[0] = addMutation.isPending;
    $[1] = onClose;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const close = t1;
  let t2;
  if ($[3] !== addMutation || $[4] !== onClose || $[5] !== pharmacy || $[6] !== setAsPrimary) {
    t2 = async () => {
      if (!pharmacy) {
        return;
      }
      try {
        await addMutation.mutateAsync({
          pharmacyId: pharmacy.pharmacyId,
          setAsPrimary
        });
        onClose();
      } catch {}
    };
    $[3] = addMutation;
    $[4] = onClose;
    $[5] = pharmacy;
    $[6] = setAsPrimary;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleAdd = t2;
  let t3;
  if ($[8] !== close) {
    t3 = open => !open && close();
    $[8] = close;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <DialogHeader><DialogTitle>Add pharmacy</DialogTitle><DialogDescription className="mt-1 text-body leading-6">Search the pharmacy directory and add it to this patient's e-prescribing record.</DialogDescription></DialogHeader>;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  let t6;
  if ($[11] !== pharmacy) {
    t5 = <PharmacySearchSelect value={pharmacy} onValueChange={setPharmacy} dataTestId="add-pharmacy-search" />;
    t6 = pharmacy && <PharmacyDetails pharmacy={pharmacy} label="Selected pharmacy" />;
    $[11] = pharmacy;
    $[12] = t5;
    $[13] = t6;
  } else {
    t5 = $[12];
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = checked => setSetAsPrimary(checked === true);
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== setAsPrimary) {
    t8 = <Checkbox id="add-pharmacy-set-primary" checked={setAsPrimary} onCheckedChange={t7} />;
    $[15] = setAsPrimary;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Label htmlFor="add-pharmacy-set-primary" className="font-normal">Set as primary pharmacy</Label>;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t8) {
    t10 = <div className="flex items-center gap-2">{t8}{t9}</div>;
    $[18] = t8;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t5 || $[22] !== t6) {
    t11 = <DialogBody className="space-y-3">{t5}{t6}{t10}</DialogBody>;
    $[20] = t10;
    $[21] = t5;
    $[22] = t6;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== addMutation.isPending || $[25] !== close) {
    t12 = <Button type="button" variant="secondary" onClick={close} disabled={addMutation.isPending}>Cancel</Button>;
    $[24] = addMutation.isPending;
    $[25] = close;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const t13 = !pharmacy;
  let t14;
  if ($[27] !== addMutation.isPending || $[28] !== handleAdd || $[29] !== t13) {
    t14 = <Button onClick={handleAdd} disabled={t13} isLoading={addMutation.isPending} loadingText="Adding...">Add pharmacy</Button>;
    $[27] = addMutation.isPending;
    $[28] = handleAdd;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t12 || $[32] !== t14) {
    t15 = <DialogFooter>{t12}{t14}</DialogFooter>;
    $[31] = t12;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t11 || $[35] !== t15) {
    t16 = <DialogContent className="sm:max-w-lg">{t4}{t11}{t15}</DialogContent>;
    $[34] = t11;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t16 || $[38] !== t3) {
    t17 = <Dialog open={true} onOpenChange={t3}>{t16}</Dialog>;
    $[37] = t16;
    $[38] = t3;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  return t17;
}